import * as utils from './common';
import { formatNumber } from '@/components/global/mixins';
import {
  csmAccountCount,
  csmBusinessValue,
  jobsAeSelectableQuotaPeriods,
  jobsDealSizes,
  jobsLeadSources,
  jobsSalesCycles,
  jobsVpDepartmentSizes,
  jobsVpManagedRoles,
  jobsVpQuotaTypes,
  salesMethodologies,
} from '@/components/partials/jobs/constants/selectableOptions';
import {
  QUOTA_ATTAINMENT_BY_TEAM_LAST_PERIOD_LABEL,
  TYPE_YOUR_ANSWER_HERE_PLACEHOLDER,
} from '@/components/partials/jobs/JobsProfile/constants';
import { getCurrencyData } from '@/components/partials/jobs/utils';
import type {
  InquiryForm,
  JobsFormFieldCompaniesSelect,
  JobsFormFieldInput,
  JobsFormFieldMultiselectWithCustomOption,
  JobsFormFieldSelect,
  JobsFormFieldSelectWithCustomOption,
  JobsSectionModels,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import {
  JobsInquiryAeQuota,
  JobsInquiryCsmQuota,
  JobsInquiryPosition,
  JobsInquirySalesMethodology,
} from '@/types/graphql';

export const getRecentClientsFieldLabel = (inquiry?: InquiryForm) => {
  const baseLabel = 'List 5-10 companies you set an appointment with in the last 2 years';
  if (!inquiry || inquiry.positionOther) {
    return baseLabel;
  }

  switch (inquiry.positionMain) {
    case JobsInquiryPosition.AccountExecutive:
      return 'List 5-10 companies you sold to in the past 2 years';
    case JobsInquiryPosition.Vp:
      return 'What are 3-5 customers you closed that you’re most proud of?';
    case JobsInquiryPosition.Sdr:
      return baseLabel;
    case JobsInquiryPosition.Csm:
      return 'What are 3-5 customers you’ve renewed or upsold?';
    default:
      return baseLabel;
  }
};

export const getQuotaTypeFormField = ({
  inquiry,
  dropdownDirection = 'bottom',
}: {
  inquiry: InquiryForm;
  dropdownDirection?: string;
}): JobsFormFieldSelectWithCustomOption => {
  return {
    attributeName: 'aeQuotaTypeMain',
    attributeNameOther: 'aeQuotaTypeOther',
    type: 'JobsSelectWithCustomOption',
    getAttrs() {
      return {
        label: 'What quota system does your company use?',
        options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(jobsAeSelectableQuotaPeriods),
        mainValue: inquiry.aeQuotaTypeMain,
        otherValue: inquiry.aeQuotaTypeOther,
        dropdownDirection,
      };
    },
  };
};

export const getVpDepartmentSizeFormField = (label: string): JobsFormFieldSelect => {
  return {
    attributeName: 'vpDepartmentSize',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        options: jobsVpDepartmentSizes.map(utils.mapOnboardingOptionsToSelectInput),
        label,
      };
    },
  };
};

export const getQuotaAmountFormField = ({ inquiry }: { inquiry: InquiryForm }): JobsFormFieldInput => {
  return {
    attributeName: 'aeQuotaPerPeriod',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label: utils.getQuotaFieldLabel(inquiry),
        placeholder: 'Type your answer here',
        valueFormatter: formatNumber.methods.numberToDelimitedFormatter,
        valueParser: formatNumber.methods.delimitedToNumberParser,
      };
    },
    icon: getCurrencyData(inquiry.currency).symbol,
    listeners: {
      keydown: formatNumber.methods.validateNumberInput,
    },
    upsertAsInt: true,
  };
};

export const getAverageDealSizeFormField = (): JobsFormFieldSelect => {
  return {
    attributeName: 'dealSize',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        label: 'What is your average deal size?',
        options: jobsDealSizes.map(utils.mapOnboardingOptionsToSelectInput),
      };
    },
  };
};

export const getBaseSalaryFormField = ({
  inquiry,
  label,
}: {
  inquiry: InquiryForm;
  label?: string;
}): JobsFormFieldInput => {
  return {
    attributeName: 'baseSalary',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label: label || utils.getBaseSalaryFieldLabel(inquiry),
        placeholder: 'Describe here',
        valueFormatter: formatNumber.methods.numberToDelimitedFormatter,
        valueParser: formatNumber.methods.delimitedToNumberParser,
      };
    },
    icon: getCurrencyData(inquiry.currency).symbol,
    listeners: {
      keydown: formatNumber.methods.validateNumberInput,
    },
    upsertAsInt: true,
  };
};

export const getOteFormField = ({ inquiry, label }: { inquiry: InquiryForm; label?: string }): JobsFormFieldInput => {
  return {
    attributeName: 'ote',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label: label || utils.getOteFieldLabel(inquiry),
        placeholder: 'Describe here',
        valueFormatter: formatNumber.methods.numberToDelimitedFormatter,
        valueParser: formatNumber.methods.delimitedToNumberParser,
      };
    },
    icon: getCurrencyData(inquiry.currency).symbol,
    listeners: {
      keydown: formatNumber.methods.validateNumberInput,
    },
    upsertAsInt: true,
  };
};

export const getRecentClientsFormField = ({ inquiry, user }: JobsSectionModels): JobsFormFieldCompaniesSelect => {
  return {
    type: 'JobsCompaniesSelect',
    attributeName: 'solidClients',
    getAttrs() {
      return {
        label: getRecentClientsFieldLabel(inquiry),
        clients: user.solidClients,
      };
    },
  };
};

export const getQuotaAttinmentByTeamFormField = (
  label: string = QUOTA_ATTAINMENT_BY_TEAM_LAST_PERIOD_LABEL,
): JobsFormFieldInput => {
  return {
    attributeName: 'quotaAttainmentByTeamPerPeriod',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label,
        placeholder: 'Describe here',
      };
    },
    upsertAsInt: true,
    getIsHidden: form => {
      if (form.positionMain !== JobsInquiryPosition.Csm) return false;

      return form.csmQuotaTypeMain === JobsInquiryCsmQuota.CsmNoQuota;
    },
  };
};

export const getRankOnTeamFormField = (label: string = 'What is your rank on the team?'): JobsFormFieldInput => {
  return {
    attributeName: 'rankOnTeam',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label,
        placeholder: 'Describe here',
      };
    },
    upsertAsInt: true,
  };
};

export const getTeamSizeFormField = (label: string = 'How many sales reps are on your team?'): JobsFormFieldInput => {
  return {
    attributeName: 'teamSize',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label,
        placeholder: 'Describe here',
      };
    },
    upsertAsInt: true,
  };
};

export const getQuotaAttainmentPerPeriodFormField = (): JobsFormFieldInput => {
  return {
    attributeName: 'quotaAttainmentPerPeriod',
    type: 'BravadoTextInputV3',
    getAttrs(form) {
      return {
        label: utils.getQuotaAttainmentEditFieldLabel(form),
        placeholder: TYPE_YOUR_ANSWER_HERE_PLACEHOLDER,
      };
    },
    getIsHidden(form) {
      return form.aeQuotaTypeMain === JobsInquiryAeQuota.Annual;
    },
    upsertAsInt: true,
  };
};

export const getQuotaAttainmentPerLastYearFormField = (): JobsFormFieldInput => {
  return {
    attributeName: 'quotaAttainmentPerLastYear',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label: 'What was your % quota attainment last year?',
        placeholder: TYPE_YOUR_ANSWER_HERE_PLACEHOLDER,
      };
    },
    upsertAsInt: true,
  };
};

export const getRevenueFormField = ({ inquiry }: { inquiry: InquiryForm }): JobsFormFieldInput => {
  return {
    attributeName: 'revenue',
    type: 'BravadoTextInputV3',
    getAttrs() {
      return {
        label: utils.getRevenueFormLabel(inquiry),
        placeholder: 'Describe here',
        valueFormatter: formatNumber.methods.numberToDelimitedFormatter,
        valueParser: formatNumber.methods.delimitedToNumberParser,
      };
    },
    icon: getCurrencyData(inquiry.currency).symbol,
    listeners: {
      keydown: formatNumber.methods.validateNumberInput,
    },
    upsertAsInt: true,
  };
};

export const getSalesCycleLengthFormField = (): JobsFormFieldSelect => {
  return {
    attributeName: 'salesCycleLength',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        label: 'What is your average sales cycle length?',
        options: jobsSalesCycles.map(utils.mapOnboardingOptionsToSelectInput),
      };
    },
  };
};

export const getSalesMethodologiesFormField = (): JobsFormFieldSelect => {
  return {
    attributeName: 'salesMethodologies',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        label: 'Which of these sales methodologies are you trained in?',
        options: salesMethodologies.map(utils.mapOnboardingOptionsToSelectInput),
        dropdownDirection: 'top',
        multi: true,
      };
    },
    inputHandler(form, value): void {
      const lastElement = value[value.length - 1];
      form.salesMethodologies =
        JobsInquirySalesMethodology.NoFormalTraining === lastElement
          ? [lastElement]
          : value.filter(salesMethodology => salesMethodology !== JobsInquirySalesMethodology.NoFormalTraining);
    },
  };
};

export const getAeLeadSourcesFormField = (inquiry: InquiryForm): JobsFormFieldMultiselectWithCustomOption => {
  return {
    attributeName: 'aeLeadSourcesMain',
    attributeNameOther: 'aeLeadSourceOther',
    type: 'JobsMultiselectWithCustomOption',
    getAttrs() {
      return {
        label: 'How do you get the majority of your leads?',
        options: jobsLeadSources.map(utils.mapOnboardingOptionsToSelectInput),
        multi: true,
        mainValue: inquiry.aeLeadSourcesMain,
        otherValue: inquiry.aeLeadSourceOther,
      };
    },
  };
};

export const getCsmAccountsNumberFormField = (): JobsFormFieldSelect => {
  return {
    attributeName: 'csmAccountsNumber',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        label: 'How many accounts are you responsible for?',
        options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(csmAccountCount),
      };
    },
  };
};

export const getCsmTotalBusinessValueFormField = (): JobsFormFieldSelect => {
  return {
    attributeName: 'csmTotalBusinessValue',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        label: 'What is the total value of your book of business?',
        options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(csmBusinessValue),
      };
    },
  };
};

export const getVpQuotaTypeFormField = (): JobsFormFieldSelect => {
  return {
    attributeName: 'vpQuotaType',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        options: jobsVpQuotaTypes.map(utils.mapOnboardingOptionsToSelectInput),
        label: 'Is your quota measured individually or per team?',
      };
    },
  };
};

export const getVpManagedRolesFormField = (): JobsFormFieldSelect => {
  return {
    attributeName: 'vpManagedRoles',
    type: 'BravadoSelectV3',
    getAttrs() {
      return {
        options: jobsVpManagedRoles.map(utils.mapOnboardingOptionsToSelectInput),
        label: 'What roles do you manage?',
        multi: true,
      };
    },
  };
};
